/*
 * This enum maintains a list of currently running a/b experiments.
 * There are strong expectations with this last so please do read this and follow the expectations.
 *
 * 1. These experiments have a defined lifetime coordinated within amplitude.
 *      When an experiment ends, the enum should be removed, and all the coordinated code should be removed
 *      only keeping around the winner of the a/b experiment.
 * 2. The naming of these keys should exactly match the name of the test within amplitude.
 *      This will keep consistency, and improve searchability while tracking down experiments from amplitude to the code.
 * 3. Each key should be surround by a comment block, explaining the experiment and any information related to the code
 *      that a developer may benefit from when trying to trace logic through the codebase. Remember experiments can introduce
 *      bugs to code outside of the experiment, and its important that we keep the business logic changes as thoroughly explained
 *      as possible.
 */

// 🚨🚨🚨 IMPORTANT STABILITY NOTE WITH EXPERIMENTS 🚨🚨🚨
// Experiments must be "safe" by default.
// Never add or use an experiment in a way that causes the app to fail to function properly if:
// - Amplitude network call fails etc.
// - Amplitude loads slowly (remember the app load does NOT block on amplitude)
// - Amplitude value changes during use of the app (remember the app load does NOT block on amplitude and it can be slow to load)
// - Our Amplitude wrapper caches an empty object in local storage from a previous failure
// See here for an example that caused the cart to not display https://github.com/rbilabs/bk-us-app/pull/266/files

export enum RunningExperiments {
  // Ongoing Menu A/B Experiment
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/110962/configure
  OngoingMenuExperiment = 'menu-a-b',

  // Notifications Center Test
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/322775/settings
  // FIXME: Remove after completing the experiment
  // TODO PER PRODUCT: NEED TO RETEST THIS
  NotificationCenterTest = 'notification-center-test',

  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/386109/settings
  // FIXME: Remove after completing the experiment
  CheckoutV2 = 'enable-checkout-v2',

  // Minimal Cart Experiment
  // Enables the minimal cart experience and only logs exposure from the first eligible cart surface visited in an Amplitude session.
  // FIXME: Remove after completing the experiment
  EnableMinimalCart = 'enable-minimal-cart',

  // DEV https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/417798/settings
  // PROD https://app.amplitude.com/experiment/restaurantbrandsintl/254852/config/418289/settings
  DisableCartBackGesture = 'cart-back-gesture',

  // Sign In/Up After Cart Experiment
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/412828/settings
  // This experiment will allow users in treatment to view their cart and order subtotal before having to authenticate.
  SignInUpAfterCart = 'sign-in-up-after-cart',

  // Location modal with map
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/570905/settings
  // This experiment will allow users in treatment to view the map in the auto select closest store prompt modal
  LocationModalWithMap = 'location-modal-with-map',

  // Homepage Hero Image A/B/C Test
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/787652/settings
  // Tests 3 different homepage hero images for Maple Bourbon Whopper campaign.
  HomepageHeroImage = 'homepage-hero-image-a-b-c-experiment',

  // Modifier redesign phase 2
  // @see https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/1125304/settings
  // This experiment will update the modifier UI to match the new design.
  ModifierRedesignPhase2 = 'modifier-redesign-phase-2',

  // Render-ready offers & rewards
  // Serves render-ready offers and rewards from the loyalty service instead of the
  // legacy Sanity + loyalty-engine filtering/transform pipeline.
  // Gated alongside the ENABLE_LOYALTY_SERVICE_OFFERS / ENABLE_LOYALTY_SERVICE_REWARDS
  // LD kill-switches; see the loyalty-service offers/rewards gate hooks.
  // DEV  https://app.amplitude.com/experiment/restaurantbrandsintl/253543/config/1165038/settings
  // PROD https://app.amplitude.com/experiment/restaurantbrandsintl/254852/config/1174363/settings
  // FIXME: Remove after completing the experiment
  EnableRenderReadyOffersRewards = 'enable-render-ready-offers-rewards',

  // Order in-progress modal experiment
  // Treatment enables pending-order persistence, recovery UI, and checkout back-navigation guards.
  // FIXME: Remove after completing the experiment
  OrderPendingProcessModal = 'order-in-progress-modal',

  // Closer-store coachmark in checkout
  // Treatment shows the non-blocking closer-restaurant coachmark on pickup checkout when a
  // nearer eligible restaurant exists than the selected store. Gates the coachmark in
  // use-closer-restaurant-coachmark; exposure logs once per Amplitude session, for both
  // treatment and control, but only once the guest's eligibility for the coachmark
  // (structural preconditions met and a closer restaurant found) is actually known and true -
  // ineligible guests in either arm are never exposed. Eligibility is evaluated identically
  // for both arms, so control runs the same nearby-store check even though it never renders
  // the coachmark UI.
  // FIXME: Remove after completing the experiment
  ShowCloserStoreCoachmarkInCheckout = 'show-closer-store-coachmark-in-checkout',

  // Apple MapKit JS vs Google Maps on web
  // Treatment serves MapKit on all web map surfaces; control keeps Google Maps.
  // Safe default = control (Google) when Amplitude is slow or fails.
  // Traffic split and sticky assignment are configured in Amplitude (target 20% / 80%).
  // @see Amplitude dashboard — add DEV/PROD settings URLs when the experiment is created
  // FIXME: Remove after completing the experiment
  EnableAppleMapkitWeb = 'enable-apple-mapkit-web',
}
